import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import QRCode from 'qrcode'
import { logger } from '@/utils/logger'

interface ABTStyleControlsProps {
  viewer: any
  onAnnotationToolChange?: (tool: string | null) => void
  selectedAnnotationTool?: string | null
  onClearAnnotations?: () => void
  onSaveAnnotations?: () => void
  hasUnsavedAnnotations?: boolean
  currentImageId?: string
  hiddenAnnotations?: Set<string>
  onToggleAnnotationVisibility?: (id: string) => void
  onToggleAIPanel?: () => void
  showAIPanel?: boolean
  onToggleAnnotationTool?: () => void
  showAnnotationTool?: boolean
  onToggleReportPanel?: () => void
  showReportPanel?: boolean
  onToggleSnapshotPanel?: () => void
  showSnapshotPanel?: boolean
  onToggleMultiView?: () => void
  showMultiView?: boolean
  // Controlled adjustment values for multi-view
  controlledAdjustments?: {
    brightness?: number
    contrast?: number
    saturation?: number
    gamma?: number
    redChannel?: number
    greenChannel?: number
    blueChannel?: number
  }
  onAdjustmentChange?: (adjustments: {
    brightness?: number
    contrast?: number
    saturation?: number
    gamma?: number
    redChannel?: number
    greenChannel?: number
    blueChannel?: number
  }) => void
}

const ABTStyleControls: React.FC<ABTStyleControlsProps> = ({
  viewer,
  onAnnotationToolChange,
  selectedAnnotationTool: externalSelectedTool,
  onClearAnnotations,
  onSaveAnnotations,
  hasUnsavedAnnotations = false,
  currentImageId,
  hiddenAnnotations: externalHiddenAnnotations,
  onToggleAnnotationVisibility: externalToggleVisibility,
  onToggleAIPanel: _onToggleAIPanel,
  showAIPanel: _showAIPanel = false,
  onToggleAnnotationTool: _onToggleAnnotationTool,
  showAnnotationTool: _showAnnotationTool = false,
  onToggleReportPanel,
  showReportPanel = false,
  onToggleSnapshotPanel,
  showSnapshotPanel = false,
  onToggleMultiView,
  showMultiView = false,
  controlledAdjustments,
  onAdjustmentChange
}) => {
  const [showColorMenu, setShowColorMenu] = useState(false)
  const [showRotateMenu, setShowRotateMenu] = useState(false)
  const [showShareMenu, setShowShareMenu] = useState(false)
  const [showAnnotationMenu, setShowAnnotationMenu] = useState(false)
  const [showImageListMenu, setShowImageListMenu] = useState(false)
  const [showAnnotationListMenu, setShowAnnotationListMenu] = useState(false)
  const [patientImages, setPatientImages] = useState<any[]>([])
  const [annotationsList, setAnnotationsList] = useState<any[]>([])
  const [loadingImages, setLoadingImages] = useState(false)
  const [loadingAnnotations, setLoadingAnnotations] = useState(false)
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('')
  const [brightness, setBrightness] = useState(100)
  const [contrast, setContrast] = useState(100)
  const [saturation, setSaturation] = useState(100)
  const [gamma, setGamma] = useState(1)
  const [redChannel, setRedChannel] = useState(100)
  const [greenChannel, setGreenChannel] = useState(100)
  const [blueChannel, setBlueChannel] = useState(100)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [rotation, setRotation] = useState(0)

  // Sync controlled adjustments from parent (for multi-view)
  useEffect(() => {
    if (controlledAdjustments) {
      if (controlledAdjustments.brightness !== undefined) setBrightness(controlledAdjustments.brightness)
      if (controlledAdjustments.contrast !== undefined) setContrast(controlledAdjustments.contrast)
      if (controlledAdjustments.saturation !== undefined) setSaturation(controlledAdjustments.saturation)
      if (controlledAdjustments.gamma !== undefined) setGamma(controlledAdjustments.gamma)
      if (controlledAdjustments.redChannel !== undefined) setRedChannel(controlledAdjustments.redChannel)
      if (controlledAdjustments.greenChannel !== undefined) setGreenChannel(controlledAdjustments.greenChannel)
      if (controlledAdjustments.blueChannel !== undefined) setBlueChannel(controlledAdjustments.blueChannel)
    }
  }, [controlledAdjustments])

  const selectedAnnotationTool = externalSelectedTool

  const handleAnnotationToolSelect = (tool: string) => {
    if (onAnnotationToolChange) {
      onAnnotationToolChange(tool)
    }
  }

  const handleClearAnnotationTool = () => {
    if (onAnnotationToolChange) {
      onAnnotationToolChange(null)
    }
  }

  const handleDeleteAllAnnotations = () => {
    if (onClearAnnotations) {
      onClearAnnotations()
      toast.success('All annotations cleared')
    }
  }

  // Color adjustments with CSS filters + SVG filter for RGB
  // NOTE: In multi-view mode, filters are applied by IndividualViewerPanel, not here
  React.useEffect(() => {
    // Skip applying filters in multi-view mode - IndividualViewerPanel handles it
    if (showMultiView) return

    if (!viewer) return
    const canvas = viewer.drawer?.canvas
    if (!canvas) return

    // Build filter string
    const filters = []

    // Base CSS filters
    filters.push(`brightness(${brightness}%)`)
    filters.push(`contrast(${contrast}%)`)
    filters.push(`saturate(${saturation}%)`)

    // Gamma approximation using contrast
    if (gamma !== 1) {
      const gammaContrast = Math.pow(100, 1 / gamma)
      filters.push(`contrast(${gammaContrast.toFixed(1)}%)`)
    }

    // RGB channel adjustments using SVG feColorMatrix
    const needsRGBFilter = redChannel !== 100 || greenChannel !== 100 || blueChannel !== 100

    if (needsRGBFilter) {
      // Create unique filter ID
      const filterId = 'osd-rgb-filter'

      // Get or create SVG container
      let svgContainer = document.getElementById('osd-rgb-svg') as SVGSVGElement | null
      if (!svgContainer) {
        svgContainer = document.createElementNS('http://www.w3.org/2000/svg', 'svg') as SVGSVGElement
        svgContainer.id = 'osd-rgb-svg'
        svgContainer.style.position = 'absolute'
        svgContainer.style.width = '0'
        svgContainer.style.height = '0'
        svgContainer.style.pointerEvents = 'none'
        document.body.appendChild(svgContainer)
      }

      // Clear previous filter
      if (svgContainer) {
        svgContainer.innerHTML = ''

        // Create filter
        const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
        const filter = document.createElementNS('http://www.w3.org/2000/svg', 'filter')
        filter.id = filterId

        // Color matrix for RGB channels
        const colorMatrix = document.createElementNS('http://www.w3.org/2000/svg', 'feColorMatrix')
        colorMatrix.setAttribute('type', 'matrix')

        const r = redChannel / 100
        const g = greenChannel / 100
        const b = blueChannel / 100

        // Color matrix (5x5): multiply each channel independently
        const matrix = `${r} 0 0 0 0  0 ${g} 0 0 0  0 0 ${b} 0 0  0 0 0 1 0`
        colorMatrix.setAttribute('values', matrix)

        filter.appendChild(colorMatrix)
        defs.appendChild(filter)
        svgContainer.appendChild(defs)

        // Add SVG filter to filter chain
        filters.push(`url(#${filterId})`)
      }
    } else {
      // Remove SVG if not needed
      const oldSvg = document.getElementById('osd-rgb-svg')
      if (oldSvg) oldSvg.remove()
    }

    // Apply all filters
    canvas.style.filter = filters.join(' ')

    // Cleanup
    return () => {
      if (!needsRGBFilter) {
        const oldSvg = document.getElementById('osd-rgb-svg')
        if (oldSvg) oldSvg.remove()
      }
    }
  }, [brightness, contrast, saturation, gamma, redChannel, greenChannel, blueChannel, viewer])

  // Rotation
  React.useEffect(() => {
    if (!viewer || !viewer.viewport) return
    viewer.viewport.setRotation(rotation)
  }, [rotation, viewer])

  // Auto-fetch patient images when currentImageId changes
  useEffect(() => {
    const fetchPatientImages = async () => {
      if (!currentImageId) return

      setLoadingImages(true)
      try {
        const slideUrl = `/api/v1/imaging/slides/${currentImageId}/`
        const slideResponse = await fetch(slideUrl)
        if (slideResponse.ok) {
          const slideData = await slideResponse.json()

          // Fetch ALL images for this patient, not just the current case
          if (slideData.case) {
            // Get patient_id from case
            const caseUrl = `/api/v1/pathology/cases/${slideData.case}/`
            const caseResponse = await fetch(caseUrl)
            if (caseResponse.ok) {
              const caseData = await caseResponse.json()
              if (caseData.patient && caseData.patient.patient_id) {
                const imagesUrl = `/api/v1/imaging/slides/?patient_id=${caseData.patient.patient_id}&page_size=1000`
                const response = await fetch(imagesUrl)
                if (response.ok) {
                  const data = await response.json()
                  setPatientImages(data.results || [])
                }
              }
            }
          } else {
            setPatientImages([slideData])
          }
        }
      } catch (error) {
        console.error('Error fetching patient images:', error)
      } finally {
        setLoadingImages(false)
      }
    }

    fetchPatientImages()
  }, [currentImageId])

  // Zoom
  const handleZoomPreset = (multiplier: number) => {
    if (!viewer) return
    const homeZoom = viewer.viewport.getHomeZoom()
    viewer.viewport.zoomTo(homeZoom * multiplier)
  }

  const handleZoomFit = () => {
    if (!viewer) return
    viewer.viewport.goHome()
  }

  // Keyboard navigation - Di chuyển 2/3 viewport, giữ lại 1/3
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!viewer) return

      // Arrow keys navigation
      if ([37, 38, 39, 40].includes(e.keyCode)) {
        e.preventDefault()
        const viewport = viewer.viewport
        const bounds = viewport.getBounds()
        const panAmount = bounds.width * 0.67 // Di chuyển 2/3 khung hình
        const center = viewport.getCenter()

        const directions: { [key: number]: any } = {
          38: new (window as any).OpenSeadragon.Point(center.x, center.y - panAmount), // Up
          40: new (window as any).OpenSeadragon.Point(center.x, center.y + panAmount), // Down
          37: new (window as any).OpenSeadragon.Point(center.x - panAmount, center.y), // Left
          39: new (window as any).OpenSeadragon.Point(center.x + panAmount, center.y)  // Right
        }

        if (directions[e.keyCode]) {
          viewport.panTo(directions[e.keyCode])
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [viewer])

  const handleShare = async () => {
    const shareUrl = window.location.href

    try {
      // Generate QR code
      const qrDataUrl = await QRCode.toDataURL(shareUrl, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      })
      setQrCodeDataUrl(qrDataUrl)
      setShowShareMenu(true)
      setShowColorMenu(false)
      setShowRotateMenu(false)
    } catch (error) {
      toast.error('Failed to generate QR code')
    }
  }

  const copyShareLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      toast.success('Link copied to clipboard!')
    }).catch(() => {
      toast.error('Failed to copy link')
    })
  }

  const handleScreenshot = () => {
    if (!viewer) return
    try {
      const canvas = viewer.drawer.canvas
      canvas.toBlob((blob: Blob | null) => {
        if (blob) {
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `screenshot-${Date.now()}.png`
          a.click()
          URL.revokeObjectURL(url)
          toast.success('Screenshot saved!')
        }
      })
    } catch {
      toast.error('Screenshot failed')
    }
  }

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
      setIsFullscreen(true)
    } else {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }

  const adjustRotation = (delta: number) => {
    setRotation((prev) => (prev + delta + 360) % 360)
  }

  // Toggle Image List menu (data is fetched automatically by useEffect)
  const handleImageListClick = () => {
    const newState = !showImageListMenu
    setShowImageListMenu(newState)
    setShowShareMenu(false)
    setShowColorMenu(false)
    setShowRotateMenu(false)
    setShowAnnotationMenu(false)
    setShowAnnotationListMenu(false)
  }

  // Fetch annotations when menu opens
  const handleAnnotationListClick = async () => {
    const newState = !showAnnotationListMenu
    setShowAnnotationListMenu(newState)
    setShowShareMenu(false)
    setShowColorMenu(false)
    setShowRotateMenu(false)
    setShowAnnotationMenu(false)
    setShowImageListMenu(false)

    if (newState && currentImageId) {
      setLoadingAnnotations(true)
      try {
        const response = await fetch(`/api/v1/pathology/annotations/?slide_id=${currentImageId}`)
        if (response.ok) {
          const data = await response.json()
          setAnnotationsList(data.results || data)
        }
      } catch (error) {
        console.error('Failed to fetch annotations:', error)
        toast.error('Failed to load annotations')
      } finally {
        setLoadingAnnotations(false)
      }
    }
  }

  // Handle toggle annotation visibility
  const handleToggleAnnotationVisibility = (annotationId: string) => {
    if (externalToggleVisibility) {
      externalToggleVisibility(annotationId)
      const isHidden = externalHiddenAnnotations?.has(annotationId)
      toast.success(isHidden ? 'Annotation shown' : 'Annotation hidden', { icon: isHidden ? '👁️' : '🙈' })
    }
  }

  const hiddenAnnotations = externalHiddenAnnotations || new Set()

  // Handle delete annotation
  const handleDeleteAnnotation = async (annotationId: string) => {
    if (!confirm('Are you sure you want to delete this annotation?')) return

    try {
      const response = await fetch(`/api/v1/pathology/annotations/${annotationId}/`, {
        method: 'DELETE',
      })

      if (response.ok) {
        setAnnotationsList(prev => prev.filter(ann => ann.id !== annotationId))
        toast.success('Annotation deleted')
      } else {
        toast.error('Failed to delete annotation')
      }
    } catch (error) {
      console.error('Error deleting annotation:', error)
      toast.error('Error deleting annotation')
    }
  }

  // Handle locate annotation on image
  const handleLocateAnnotation = (annotation: any) => {
    logger.debug('🔍 handleLocateAnnotation called with:', annotation)
    const title = annotation.text_content || `${annotation.annotation_type} annotation`

    try {
      if (!annotation.coordinates) {
        console.error('❌ No coordinates:', annotation)
        toast('Annotation coordinates not available', { icon: '⚠️' })
        return
      }

      if (!viewer) {
        console.error('❌ Viewer not available')
        toast('Viewer not ready', { icon: '⚠️' })
        return
      }

      if (!window.OpenSeadragon) {
        console.error('❌ OpenSeadragon not loaded')
        toast('OpenSeadragon not loaded', { icon: '⚠️' })
        return
      }

      if (viewer && window.OpenSeadragon) {
        const coords = annotation.coordinates
        let centerX: number, centerY: number

        // Calculate center point based on shape type
        if (coords.type === 'rectangle') {
          centerX = coords.x + coords.width / 2
          centerY = coords.y + coords.height / 2
        } else if (coords.type === 'circle') {
          centerX = coords.cx
          centerY = coords.cy
        } else if (coords.type === 'ellipse') {
          centerX = coords.cx
          centerY = coords.cy
        } else if (coords.type === 'arrow' || coords.type === 'line') {
          centerX = (coords.x1 + coords.x2) / 2
          centerY = (coords.y1 + coords.y2) / 2
        } else {
          toast('Unsupported annotation type', { icon: '⚠️' })
          return
        }

        // Convert image pixel coordinates to viewport coordinates
        const imagePoint = new window.OpenSeadragon.Point(centerX, centerY)
        const viewportPoint = viewer.viewport.imageToViewportCoordinates(imagePoint)

        logger.debug('🎯 Locating annotation:', coords.type, 'at image pixels:', centerX, centerY, '→ viewport:', viewportPoint.x, viewportPoint.y)

        // Pan to the annotation location
        viewer.viewport.panTo(viewportPoint, true)

        // Zoom in to focus on the annotation
        const currentZoom = viewer.viewport.getZoom()
        const targetZoom = Math.max(currentZoom, viewer.viewport.getHomeZoom() * 4)
        viewer.viewport.zoomTo(targetZoom, viewportPoint, true)

        toast.success(`Located: ${title}`, { icon: '🎯' })
      }
    } catch (error) {
      console.error('Error locating annotation:', error)
      toast.error('Failed to locate annotation')
    }
  }

  return (
    <>
      {/* Right Sidebar Menu - similar to KFBio leftmenu */}
      <div
        className="absolute right-0 top-0 bottom-0 w-16 z-50 flex flex-col items-center py-4 gap-2"
        style={{
          background: '#298eff',
          opacity: 0.9,
          boxShadow: '0 0 8px rgba(0,0,0,.15)'
        }}
      >
        {/* Image List - New button at top */}
        <div className="relative">
          <button
            onClick={handleImageListClick}
            className={`w-12 h-12 flex flex-col items-center justify-center hover:bg-blue-600 transition-colors rounded ${showImageListMenu ? 'bg-blue-600' : ''}`}
            title={`Image List (${patientImages.length})`}
          >
            <svg className="w-5 h-5 text-white mb-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
            <span className="text-[10px] text-white font-medium">{patientImages.length}</span>
          </button>

          {showImageListMenu && (
            <div className="fixed right-24 top-4 bottom-4 bg-white rounded-lg shadow-xl overflow-hidden border-2 border-blue-500" style={{ width: '360px', display: 'flex', flexDirection: 'column', zIndex: 9999 }}>
              {/* Sticky Header */}
              <div className="flex items-center justify-between px-3 py-3 bg-blue-50 border-b-2 border-blue-300 flex-shrink-0">
                <h3 className="font-bold text-base text-blue-900">Patient Images ({patientImages.length})</h3>
                <button
                  onClick={() => setShowImageListMenu(false)}
                  className="text-gray-600 hover:text-gray-800"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-3">
              {loadingImages ? (
                <div className="text-center text-gray-600 text-sm py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  Loading images...
                </div>
              ) : patientImages.length === 0 ? (
                <div className="text-center text-gray-500 text-sm py-8">
                  No images found
                </div>
              ) : (
                <div className="space-y-2">
                  {patientImages.map((image) => (
                    <a
                      key={image.id}
                      href={`/imaging/viewer/${image.id}`}
                      className={`block p-2 rounded-lg hover:bg-blue-50 transition-colors ${
                        currentImageId === image.id ? 'bg-blue-100 border-2 border-blue-500' : 'border border-gray-200'
                      }`}
                    >
                      <div className="flex gap-3">
                        {/* Thumbnail */}
                        <div className="flex-shrink-0 w-20 h-20 bg-gray-100 rounded overflow-hidden">
                          <img
                            src={`/api/v1/imaging/slides/${image.id}/thumbnail/?size=128`}
                            alt={image.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80"%3E%3Crect fill="%23ddd" width="80" height="80"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E'
                            }}
                          />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm text-gray-800 truncate">{image.name}</h4>
                          <p className="text-xs text-gray-500 mt-1">
                            {image.width} × {image.height}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(image.file_size / 1024 / 1024).toFixed(1)} MB
                          </p>
                          <div className={`text-xs mt-1 inline-block px-2 py-0.5 rounded ${
                            image.status === 'ready' ? 'bg-green-100 text-green-700' :
                            image.status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {image.status}
                          </div>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              )}
              </div>
            </div>
          )}
        </div>

        {/* Annotations List - New button */}
        <div className="relative">
          <button
            onClick={handleAnnotationListClick}
            className={`w-12 h-12 flex flex-col items-center justify-center hover:bg-blue-600 transition-colors rounded ${showAnnotationListMenu ? 'bg-blue-600' : ''}`}
            title={`Annotations (${annotationsList.length})`}
          >
            <svg className="w-5 h-5 text-white mb-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-[10px] text-white font-medium">{annotationsList.length}</span>
          </button>

          {showAnnotationListMenu && (
            <div className="fixed right-24 top-4 bottom-4 bg-white rounded-lg shadow-xl overflow-hidden" style={{ width: '360px', display: 'flex', flexDirection: 'column' }}>
              {/* Sticky Header */}
              <div className="flex items-center justify-between px-3 py-3 bg-white border-b border-gray-200 flex-shrink-0">
                <h3 className="font-bold text-sm text-gray-800">Annotations ({annotationsList.length})</h3>
                <button
                  onClick={() => setShowAnnotationListMenu(false)}
                  className="text-gray-600 hover:text-gray-800"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-3">
              {loadingAnnotations ? (
                <div className="text-center text-gray-600 text-sm py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  Loading annotations...
                </div>
              ) : annotationsList.length === 0 ? (
                <div className="text-center text-gray-500 text-sm py-8">
                  No annotations found
                </div>
              ) : (
                <div className="space-y-2">
                  {annotationsList.map((annotation) => (
                    <div
                      key={annotation.id}
                      className="block p-3 rounded-lg border border-gray-200 hover:bg-blue-50 transition-colors bg-white"
                    >
                      <div className="flex items-start gap-2">
                        {/* Annotation icon */}
                        <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded flex items-center justify-center">
                          <span className="text-green-600 text-xs font-bold uppercase">
                            {annotation.annotation_type.substring(0, 3)}
                          </span>
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm text-gray-800 truncate">
                            {annotation.text_content || `${annotation.annotation_type} annotation`}
                          </h4>
                          {annotation.description && (
                            <p className="text-xs text-gray-600 mt-1 line-clamp-2">{annotation.description}</p>
                          )}

                          {/* Coordinates and Area Info */}
                          {annotation.coordinates && annotation.coordinates.type ? (
                            <div className="mt-2 p-2 bg-gray-50 rounded text-xs font-mono">
                              {annotation.coordinates.type === 'rectangle' && (
                                <>
                                  <div>📍 X: {Math.round(annotation.coordinates.x)}, Y: {Math.round(annotation.coordinates.y)}</div>
                                  <div>📏 W: {Math.round(annotation.coordinates.width)}, H: {Math.round(annotation.coordinates.height)}</div>
                                  <div>📐 Area: {Math.round(annotation.coordinates.width * annotation.coordinates.height).toLocaleString()} px²</div>
                                </>
                              )}
                              {annotation.coordinates.type === 'circle' && (
                                <>
                                  <div>📍 Center: ({Math.round(annotation.coordinates.cx)}, {Math.round(annotation.coordinates.cy)})</div>
                                  <div>📏 Radius: {Math.round(annotation.coordinates.r)}</div>
                                  <div>📐 Area: {Math.round(Math.PI * annotation.coordinates.r * annotation.coordinates.r).toLocaleString()} px²</div>
                                </>
                              )}
                              {annotation.coordinates.type === 'ellipse' && (
                                <>
                                  <div>📍 Center: ({Math.round(annotation.coordinates.cx)}, {Math.round(annotation.coordinates.cy)})</div>
                                  <div>📏 Rx: {Math.round(annotation.coordinates.rx)}, Ry: {Math.round(annotation.coordinates.ry)}</div>
                                  <div>📐 Area: {Math.round(Math.PI * annotation.coordinates.rx * annotation.coordinates.ry).toLocaleString()} px²</div>
                                </>
                              )}
                              {(annotation.coordinates.type === 'arrow' || annotation.coordinates.type === 'line') && (
                                <>
                                  <div>📍 Start: ({Math.round(annotation.coordinates.x1)}, {Math.round(annotation.coordinates.y1)})</div>
                                  <div>📍 End: ({Math.round(annotation.coordinates.x2)}, {Math.round(annotation.coordinates.y2)})</div>
                                  <div>📏 Length: {Math.round(Math.sqrt(Math.pow(annotation.coordinates.x2 - annotation.coordinates.x1, 2) + Math.pow(annotation.coordinates.y2 - annotation.coordinates.y1, 2)))} px</div>
                                </>
                              )}
                            </div>
                          ) : (
                            <div className="mt-2 p-2 bg-yellow-50 rounded text-xs text-yellow-700">
                              ⚠️ No coordinate data available
                            </div>
                          )}

                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-gray-500">
                              {new Date(annotation.created_at).toLocaleDateString()}
                            </span>
                            <div className={`text-xs px-2 py-0.5 rounded ${
                              annotation.annotation_type === 'arrow' ? 'bg-blue-100 text-blue-700' :
                              annotation.annotation_type === 'rectangle' ? 'bg-purple-100 text-purple-700' :
                              annotation.annotation_type === 'circle' ? 'bg-pink-100 text-pink-700' :
                              annotation.annotation_type === 'ellipse' ? 'bg-indigo-100 text-indigo-700' :
                              annotation.annotation_type === 'pen' ? 'bg-orange-100 text-orange-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {annotation.annotation_type}
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex-shrink-0 flex flex-col gap-1">
                          {/* Locate button - GPS/pin icon */}
                          <button
                            onClick={() => handleLocateAnnotation(annotation)}
                            className="p-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                            title="Locate on image"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </button>
                          {/* Hide/Show button - eye icon */}
                          <button
                            onClick={() => handleToggleAnnotationVisibility(annotation.id)}
                            className={`p-1.5 text-white rounded transition-colors ${
                              hiddenAnnotations.has(annotation.id)
                                ? 'bg-gray-500 hover:bg-gray-600'
                                : 'bg-green-500 hover:bg-green-600'
                            }`}
                            title={hiddenAnnotations.has(annotation.id) ? 'Show annotation' : 'Hide annotation'}
                          >
                            {hiddenAnnotations.has(annotation.id) ? (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            )}
                          </button>
                          {/* Delete button */}
                          <button
                            onClick={() => handleDeleteAnnotation(annotation.id)}
                            className="p-1.5 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                            title="Delete annotation"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              </div>
            </div>
          )}
        </div>

        {/* Share/Barcode */}
        <div className="relative">
          <button
            onClick={handleShare}
            className={`w-12 h-12 flex flex-col items-center justify-center hover:bg-blue-600 transition-colors rounded ${showShareMenu ? 'bg-blue-600' : ''}`}
            title="Share"
          >
            <svg className="w-6 h-6 text-white mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
          </button>

          {showShareMenu && (
            <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 bg-blue-600 rounded-lg p-4 text-white shadow-xl" style={{ width: '280px' }}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-sm">Share Slide</h3>
                <button
                  onClick={() => setShowShareMenu(false)}
                  className="text-white hover:text-gray-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {qrCodeDataUrl && (
                <div className="bg-white p-3 rounded mb-3 flex justify-center">
                  <img src={qrCodeDataUrl} alt="QR Code" className="w-48 h-48" />
                </div>
              )}

              <div className="text-center mb-3">
                <p className="text-xs mb-2">Scan QR code or copy link to share</p>
                <div className="bg-blue-700 rounded p-2 mb-2 text-xs break-all">
                  {window.location.href}
                </div>
                <button
                  onClick={copyShareLink}
                  className="w-full px-4 py-2 bg-white text-blue-600 rounded hover:bg-gray-100 font-medium text-sm"
                >
                  📋 Copy Link
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Annotation Tools */}
        <div className="relative">
          <button
            onClick={() => {
              setShowAnnotationMenu(!showAnnotationMenu)
              setShowColorMenu(false)
              setShowRotateMenu(false)
              setShowShareMenu(false)
            }}
            className={`w-12 h-12 flex flex-col items-center justify-center hover:bg-blue-600 transition-colors rounded ${showAnnotationMenu ? 'bg-blue-600' : ''}`}
            title="Annotation Tools"
          >
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>

          {showAnnotationMenu && (
            <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 bg-blue-600 rounded-lg p-3 text-white shadow-xl" style={{ width: '200px' }}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-sm">Annotations</h3>
                <button
                  onClick={() => setShowAnnotationMenu(false)}
                  className="text-white hover:text-gray-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-3 gap-2 mb-3">
                {/* Arrow */}
                <button
                  onClick={() => handleAnnotationToolSelect('arrow')}
                  className={`p-2 rounded hover:bg-blue-700 ${selectedAnnotationTool === 'arrow' ? 'bg-blue-700' : 'bg-blue-500'}`}
                  title="Arrow"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </button>

                {/* Rectangle */}
                <button
                  onClick={() => handleAnnotationToolSelect('rectangle')}
                  className={`p-2 rounded hover:bg-blue-700 ${selectedAnnotationTool === 'rectangle' ? 'bg-blue-700' : 'bg-blue-500'}`}
                  title="Rectangle"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <rect x="4" y="4" width="16" height="16" strokeWidth={2} />
                  </svg>
                </button>

                {/* Circle */}
                <button
                  onClick={() => handleAnnotationToolSelect('circle')}
                  className={`p-2 rounded hover:bg-blue-700 ${selectedAnnotationTool === 'circle' ? 'bg-blue-700' : 'bg-blue-500'}`}
                  title="Circle"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="8" strokeWidth={2} />
                  </svg>
                </button>

                {/* Ellipse */}
                <button
                  onClick={() => handleAnnotationToolSelect('ellipse')}
                  className={`p-2 rounded hover:bg-blue-700 ${selectedAnnotationTool === 'ellipse' ? 'bg-blue-700' : 'bg-blue-500'}`}
                  title="Ellipse"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <ellipse cx="12" cy="12" rx="9" ry="6" strokeWidth={2} />
                  </svg>
                </button>

                {/* Pen/Freehand */}
                <button
                  onClick={() => handleAnnotationToolSelect('pen')}
                  className={`p-2 rounded hover:bg-blue-700 ${selectedAnnotationTool === 'pen' ? 'bg-blue-700' : 'bg-blue-500'}`}
                  title="Pen"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>

                {/* Text/Pin */}
                <button
                  onClick={() => handleAnnotationToolSelect('text')}
                  className={`p-2 rounded hover:bg-blue-700 ${selectedAnnotationTool === 'text' ? 'bg-blue-700' : 'bg-blue-500'}`}
                  title="Text"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                </button>
              </div>

              <div className="border-t border-blue-500 pt-2 space-y-2">
                {/* Save button */}
                <button
                  onClick={onSaveAnnotations}
                  disabled={!hasUnsavedAnnotations}
                  className={`w-full px-3 py-2 text-sm font-bold rounded transition-all ${
                    hasUnsavedAnnotations
                      ? 'bg-green-500 text-white hover:bg-green-600 shadow-lg animate-pulse'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  title={hasUnsavedAnnotations ? 'Save annotations to database' : 'No unsaved changes'}
                >
                  {hasUnsavedAnnotations ? '💾 Save Annotations' : '✓ Saved'}
                </button>

                <div className="flex gap-2">
                  <button
                    onClick={handleClearAnnotationTool}
                    className="flex-1 px-3 py-1.5 text-xs bg-white text-blue-600 rounded hover:bg-gray-100 font-medium"
                  >
                    Clear Tool
                  </button>
                  <button
                    onClick={handleDeleteAllAnnotations}
                    className="flex-1 px-3 py-1.5 text-xs bg-red-500 text-white rounded hover:bg-red-600 font-medium"
                    title="Delete All"
                  >
                    Delete All
                  </button>
                </div>
              </div>

              {selectedAnnotationTool && (
                <div className="mt-2 text-xs text-center bg-blue-700 rounded p-2">
                  Selected: <strong className="capitalize">{selectedAnnotationTool}</strong>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Rotate */}
        <div className="relative">
          <button
            onClick={() => {
              setShowRotateMenu(!showRotateMenu)
              setShowColorMenu(false)
            }}
            className={`w-12 h-12 flex flex-col items-center justify-center hover:bg-blue-600 transition-colors rounded ${showRotateMenu ? 'bg-blue-600' : ''}`}
            title="Rotate"
          >
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>

          {showRotateMenu && (
            <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 bg-blue-500 rounded-lg p-3 text-white">
              <div className="flex flex-col items-center gap-2">
                <button
                  onClick={() => adjustRotation(90)}
                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm font-medium"
                >
                  +90°
                </button>
                <div className="text-sm font-bold">{rotation.toFixed(0)}°</div>
                <button
                  onClick={() => adjustRotation(-90)}
                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm font-medium"
                >
                  -90°
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Screenshot */}
        <button
          onClick={handleScreenshot}
          className="w-12 h-12 flex flex-col items-center justify-center hover:bg-blue-600 transition-colors rounded"
          title="Screenshot"
        >
          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>

        {/* Image Adjustment */}
        <div className="relative">
          <button
            onClick={() => {
              setShowColorMenu(!showColorMenu)
              setShowRotateMenu(false)
            }}
            className={`w-12 h-12 flex flex-col items-center justify-center hover:bg-blue-600 transition-colors rounded ${showColorMenu ? 'bg-blue-600' : ''}`}
            title="Image Adjustment"
          >
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </button>

          {showColorMenu && (
            <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl p-4 w-72 max-h-[80vh] overflow-y-auto">
              <h3 className="font-semibold mb-3 text-sm text-gray-800">Image Adjustment</h3>
              <div className="space-y-3">
                {/* Gamma */}
                <div className="border-b border-gray-200 pb-2">
                  <h4 className="text-xs font-bold text-gray-700 mb-2">Gamma</h4>
                  <div>
                    <label className="text-xs text-gray-600 block mb-1">Gamma: {gamma.toFixed(2)}</label>
                    <input
                      type="range"
                      min="0.1"
                      max="3"
                      step="0.1"
                      value={gamma}
                      onChange={(e) => {
                        const value = Number(e.target.value)
                        setGamma(value)
                        if (onAdjustmentChange) {
                          onAdjustmentChange({ gamma: value })
                        }
                      }}
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Contrast & Brightness */}
                <div className="border-b border-gray-200 pb-2">
                  <h4 className="text-xs font-bold text-gray-700 mb-2">Contrast & Brightness</h4>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">Contrast: {contrast}%</label>
                      <input
                        type="range"
                        min="0"
                        max="200"
                        value={contrast}
                        onChange={(e) => {
                          const value = Number(e.target.value)
                          setContrast(value)
                          if (onAdjustmentChange) {
                            onAdjustmentChange({ contrast: value })
                          }
                        }}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">Brightness: {brightness}%</label>
                      <input
                        type="range"
                        min="0"
                        max="200"
                        value={brightness}
                        onChange={(e) => {
                          const value = Number(e.target.value)
                          setBrightness(value)
                          if (onAdjustmentChange) {
                            onAdjustmentChange({ brightness: value })
                          }
                        }}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                {/* Channels Color */}
                <div className="border-b border-gray-200 pb-2">
                  <h4 className="text-xs font-bold text-gray-700 mb-2">Channels Color</h4>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-red-600 font-semibold block mb-1">Red: {redChannel}%</label>
                      <input
                        type="range"
                        min="0"
                        max="200"
                        value={redChannel}
                        onChange={(e) => {
                          const value = Number(e.target.value)
                          setRedChannel(value)
                          if (onAdjustmentChange) {
                            onAdjustmentChange({ redChannel: value })
                          }
                        }}
                        className="w-full accent-red-500"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-green-600 font-semibold block mb-1">Green: {greenChannel}%</label>
                      <input
                        type="range"
                        min="0"
                        max="200"
                        value={greenChannel}
                        onChange={(e) => {
                          const value = Number(e.target.value)
                          setGreenChannel(value)
                          if (onAdjustmentChange) {
                            onAdjustmentChange({ greenChannel: value })
                          }
                        }}
                        className="w-full accent-green-500"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-blue-600 font-semibold block mb-1">Blue: {blueChannel}%</label>
                      <input
                        type="range"
                        min="0"
                        max="200"
                        value={blueChannel}
                        onChange={(e) => {
                          const value = Number(e.target.value)
                          setBlueChannel(value)
                          if (onAdjustmentChange) {
                            onAdjustmentChange({ blueChannel: value })
                          }
                        }}
                        className="w-full accent-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Saturation (kept for compatibility) */}
                <div className="pb-2">
                  <div>
                    <label className="text-xs text-gray-600 block mb-1">Saturation: {saturation}%</label>
                    <input
                      type="range"
                      min="0"
                      max="200"
                      value={saturation}
                      onChange={(e) => {
                        const value = Number(e.target.value)
                        setSaturation(value)
                        if (onAdjustmentChange) {
                          onAdjustmentChange({ saturation: value })
                        }
                      }}
                      className="w-full"
                    />
                  </div>
                </div>

                <button
                  onClick={() => {
                    setBrightness(100)
                    setContrast(100)
                    setSaturation(100)
                    setGamma(1)
                    setRedChannel(100)
                    setGreenChannel(100)
                    setBlueChannel(100)
                    if (onAdjustmentChange) {
                      onAdjustmentChange({
                        brightness: 100,
                        contrast: 100,
                        saturation: 100,
                        gamma: 1,
                        redChannel: 100,
                        greenChannel: 100,
                        blueChannel: 100
                      })
                    }
                  }}
                  className="w-full px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded font-medium"
                >
                  Reset All
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Annotation Tool - HIDDEN */}
        {/* {onToggleAnnotationTool && (
          <button
            onClick={onToggleAnnotationTool}
            className={`w-12 h-12 flex flex-col items-center justify-center hover:bg-green-600 transition-colors rounded ${showAnnotationTool ? 'bg-green-600' : ''}`}
            title="Annotation Tool"
          >
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            <div className="text-white text-[7px] font-bold mt-[-2px]">LABEL</div>
          </button>
        )} */}

        {/* AI Diagnosis - HIDDEN */}
        {/* {onToggleAIPanel && (
          <button
            onClick={onToggleAIPanel}
            className={`w-12 h-12 flex flex-col items-center justify-center hover:bg-purple-600 transition-colors rounded ${showAIPanel ? 'bg-purple-600' : ''}`}
            title="AI Diagnosis"
          >
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="4" y="4" width="16" height="16" strokeWidth={2} rx="2" />
              <line x1="8" y1="4" x2="8" y2="2" strokeWidth={2} />
              <line x1="16" y1="4" x2="16" y2="2" strokeWidth={2} />
              <line x1="8" y1="20" x2="8" y2="22" strokeWidth={2} />
              <line x1="16" y1="20" x2="16" y2="22" strokeWidth={2} />
              <line x1="4" y1="8" x2="2" y2="8" strokeWidth={2} />
              <line x1="4" y1="16" x2="2" y2="16" strokeWidth={2} />
              <line x1="20" y1="8" x2="22" y2="8" strokeWidth={2} />
              <line x1="20" y1="16" x2="22" y2="16" strokeWidth={2} />
              <circle cx="12" cy="12" r="3" strokeWidth={2} />
            </svg>
            <div className="text-white text-[8px] font-bold mt-[-4px]">AI</div>
          </button>
        )} */}

        {/* Report Panel */}
        {onToggleReportPanel && (
          <button
            onClick={onToggleReportPanel}
            className={`w-12 h-12 flex flex-col items-center justify-center hover:bg-blue-600 transition-colors rounded ${showReportPanel ? 'bg-blue-600' : ''}`}
            title="Create Report"
          >
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <div className="text-white text-[7px] font-bold mt-[-2px]">REPORT</div>
          </button>
        )}

        {onToggleSnapshotPanel && (
          <button
            onClick={onToggleSnapshotPanel}
            className={`w-12 h-12 flex flex-col items-center justify-center hover:bg-purple-600 transition-colors rounded ${showSnapshotPanel ? 'bg-purple-600' : ''}`}
            title="Snapshot Manager"
          >
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <div className="text-white text-[7px] font-bold mt-[-2px]">SNAPSHOT</div>
          </button>
        )}

        {/* Multi-View Button */}
        {onToggleMultiView && (
          <button
            onClick={onToggleMultiView}
            className={`w-12 h-12 flex flex-col items-center justify-center hover:bg-orange-600 transition-colors rounded ${showMultiView ? 'bg-orange-600' : ''}`}
            title="Multi-View (View multiple images)"
          >
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
            </svg>
            <div className="text-white text-[7px] font-bold mt-[-2px]">VIEW</div>
          </button>
        )}
      </div>

      {/* Vertical Zoom Slider - moved to middle height - hidden in multi-view */}
      {!showMultiView && (
        <div
          className="fixed z-40 flex flex-col gap-2"
          style={{
            top: '50%',
            transform: 'translateY(-50%)',
            left: '16px',
            opacity: 0.9
          }}
        >
        {/* Zoom Indicator */}
        <div className="px-2 py-1 bg-white border border-blue-500 rounded text-blue-600 font-bold text-xs min-w-[40px] text-center shadow-lg">
          {viewer ? `${(viewer.viewport.getZoom() / viewer.viewport.getHomeZoom()).toFixed(1)}x` : '1.0x'}
        </div>

        {/* Zoom Levels - from bottom to top: Fit, 4X, 10X, ... 100X */}
        <button onClick={() => handleZoomPreset(100)} className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded font-medium min-w-[40px] shadow-lg">100X</button>
        <button onClick={() => handleZoomPreset(80)} className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded font-medium min-w-[40px] shadow-lg">80X</button>
        <button onClick={() => handleZoomPreset(60)} className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded font-medium min-w-[40px] shadow-lg">60X</button>
        <button onClick={() => handleZoomPreset(40)} className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded font-medium min-w-[40px] shadow-lg">40X</button>
        <button onClick={() => handleZoomPreset(20)} className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded font-medium min-w-[40px] shadow-lg">20X</button>
        <button onClick={() => handleZoomPreset(10)} className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded font-medium min-w-[40px] shadow-lg">10X</button>
        <button onClick={() => handleZoomPreset(4)} className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded font-medium min-w-[40px] shadow-lg">4X</button>
        <button onClick={handleZoomFit} className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded font-medium min-w-[40px] shadow-lg">Fit</button>

        {/* Fullscreen button - same width as zoom buttons */}
        <button
          onClick={toggleFullscreen}
          className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded font-medium min-w-[40px] shadow-lg mt-2 flex items-center justify-center"
        >
          {isFullscreen ? (
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          )}
        </button>
        </div>
      )}
    </>
  )
}

export default ABTStyleControls
